<template>
  <div
    ref="scrollContainer"
    :aria-label="label"
    aria-roledescription="carousel"
    class="auto-scroll-strip"
    role="region"
    @focusin="pauseAuto"
    @focusout="scheduleResume"
    @mouseenter="pauseAuto"
    @mouseleave="scheduleResume"
    @pointerdown="pauseAuto"
    @pointerup="scheduleResume"
    @scroll.passive="onScroll"
    @wheel.passive="onUserInteraction"
  >
    <div class="auto-scroll-strip__track">
      <div
        v-for="(item, index) in loopedItems"
        :key="`${itemKey(item, index)}-${index}`"
        :inert="index >= items.length"
      >
        <slot :item="item" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed, useTemplateRef } from "vue";

import { useAutoScrollCarousel } from "@solvari/common-fe/composables";

const props = withDefaults(
  defineProps<{
    itemKey: (item: T, index: number) => number | string;
    items: T[];
    label?: string;
    resumeDelay?: number;
    scrollSpeed?: number;
  }>(),
  { label: undefined, resumeDelay: 500, scrollSpeed: 0.5 },
);

const loopedItems = computed<T[]>(() => [...props.items, ...props.items]);

const scrollContainerRef = useTemplateRef<HTMLDivElement>("scrollContainer");

const { onScroll, onUserInteraction, pauseAuto, scheduleResume } =
  useAutoScrollCarousel(scrollContainerRef, {
    animationDelay: props.resumeDelay,
    scrollSpeed: props.scrollSpeed,
  });
</script>

<style lang="postcss">
.auto-scroll-strip {
  @apply min-w-0 overflow-x-auto overflow-y-hidden;
  scrollbar-width: none; /* stylelint-disable-line plugin/use-baseline */

  &::-webkit-scrollbar {
    @apply hidden;
  }

  &__track {
    @apply flex w-0 gap-2;
  }
}
</style>
